/**
 * voice-v8 — das Grundgerüst JEDER Karte im Verlauf. (14.10.2026)
 *
 * Martin, mit Bild (Rechnungskarte und „Route · 2-mal“ darunter): *„Kann man
 * bitte diese verdammten Cards endlich einmal schön machen? … dass alle, alle,
 * ich meine ausschließlich alle Cards und alle zukünftigen sauber formatiert
 * werden.“*
 *
 * Gemessen vorher (WebKit, 390 px): Knöpfe aus `<a>` 38 Punkte hoch, aus
 * `<button>` 34; der Trenner über einer Hinweiszeile nur so breit wie ihr
 * Text (`align-self: flex-start` auf der Zeile); vier Karten mit eigenem
 * Rahmen („compact“, Terminal-Grund, schmale Quittung); drei Stellen, die
 * denselben Titel bauten; zwei Betrags-Klassen für dieselbe Zahl.
 *
 * Darum baut jede Karte aus DIESEN Bausteinen — und nur aus ihnen:
 *
 *   baueKarte      die Wurzel (`.tool-card`, role=article)
 *   kartenTitel    Kopfzeile, wahlweise mit Art davor („Rechnung “)
 *   wertZeile      Beschriftung | Wert
 *   betragZeile    die eine große Zahl samt Nebenzeile
 *   chips          Stand-Pillen („Entwurf“, „läuft“)
 *   absatz         Fließtext, wahlweise als Warnung / Hinweis / Ergebnis
 *   aktionsLeiste + aktionsKnopf   die Knopfreihe, Link oder Knopf, GLEICH hoch
 *   liste + listenEintrag + weitereHinweis   Einträge mit „und N weitere“
 *   leerHinweis / hinweisKarte     die leise Zeile im Inneren / als ganze Karte
 *   fehlerBlock    der eine Fehlerkasten
 *   linkZeile      „öffnet im neuen Tab“
 *   gruppenKopf    der Kopf einer Faltung („Websuche · 5-mal“)
 *
 *   dateiZeile     eine Datei: Name sichtbar, Ort in Worten, der Pfad nur im Tooltip
 *
 * Die Gestalt dazu steht in EINEM Block in style.css („KARTEN-GRUNDGERÜST“).
 * Eine Karte trägt keinen eigenen Stil (kein `style=`), keinen eigenen Rahmen
 * und keine zweite Klasse für dieselbe Sache — tests/frontend/karten-grundgeruest.test.ts
 * baut jede registrierte Karte und prüft genau das.
 *
 * ## Und der Wortlaut (15.10.2026)
 *
 * Martin, mit Bild: *„Wie kann es zu solchen Darstellungen kommen, mit diesen
 * komischen Pfaden, Buttons und allem?“* — „/Users/…/haus.blend“ als Text,
 * „NICHTS in Blender ausgeführt. Frag den Nutzer …“ als Kartentext. Das
 * Grundgerüst prüfte die Gestalt, nicht die Worte. Seitdem geht JEDER Text
 * eines Bausteins durch `fuerMenschen` (lib/karten-text.ts): kein roher
 * Pfad, kein Satz an das Modell. Und `kartenTextSaeubern` geht am Ende über
 * die fertige Karte — für alles, was an den Bausteinen vorbei `textContent`
 * setzt. tests/frontend/karten-ohne-pfade.test.ts prüft das an jeder Karte.
 */
import { icon, type IconName } from "./icons.js";
import { dateiName, dateiOrt, fuerMenschen, ohnePfade, textknotenFuerMenschen } from "./karten-text.js";

/** Die Klasse jeder Kartenwurzel. */
export const KARTE_KLASSE = "tool-card";

/**
 * Das Stück eines Titels, das nur sagt, WAS die Karte ist („Rechnung “).
 * In einer Faltung nennt das der Kopf — das Stück verstummt dann (schritte.ts).
 */
export const ART_KLASSE = "tool-card-art";

/** Die Wurzel. `art` wird zur Kennung `tool-<art>` — nur für Tests und Zugriffe, nie für eigenen Stil. */
export function baueKarte(art: string, titel?: string): HTMLDivElement {
  const el = document.createElement("div");
  el.className = `${KARTE_KLASSE} tool-${art}`;
  el.setAttribute("role", "article");
  if (titel !== undefined) el.appendChild(kartenTitel(titel));
  return el;
}

/**
 * Bunte Emojis aus Titeln nehmen (Martin 2026-07-09: „keine depperten Emojis“)
 * — auch KI-erzeugte. Pfeile (→) und Typografie bleiben.
 *
 * ⚠️ Variantenwähler (FE0F) und Nullbreiten-Verbinder (200D) stehen als
 *    Alternative, nicht in der Zeichenklasse: in einer Klasse verbinden sie
 *    sich mit dem Zeichen davor (10.09.2026).
 */
export function ohneEmoji(s: string): string {
  return s
    .replace(/(?:[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\u{2300}-\u{23FF}]|️|‍)/gu, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/** Die Kopfzeile. Mit `art` steht die Art leise davor („Rechnung “ + Nummer). */
export function kartenTitel(text: string, art?: string): HTMLDivElement {
  const t = document.createElement("div");
  t.className = "tool-card-title";
  if (art) {
    const a = document.createElement("span");
    a.className = ART_KLASSE;
    a.textContent = `${fuerMenschen(ohneEmoji(art))} `;
    t.appendChild(a);
  }
  t.appendChild(document.createTextNode(fuerMenschen(ohneEmoji(text))));
  return t;
}

/** Beschriftung | Wert — eine Zeile. */
export function wertZeile(parent: HTMLElement, label: string, wert: string): HTMLDivElement {
  const row = document.createElement("div");
  row.className = "tool-kv";
  const k = document.createElement("span");
  k.className = "tool-kv-key";
  k.textContent = fuerMenschen(label);
  const v = document.createElement("span");
  v.className = "tool-kv-val";
  v.textContent = fuerMenschen(wert);
  row.append(k, v);
  parent.appendChild(row);
  return row;
}

/**
 * Eine Datei: der Name sichtbar („haus.blend“), der Ort in Worten („auf
 * deinem Mac“), der volle Pfad nur im Tooltip — nie als Fließtext.
 * (15.10.2026, Martin: „mit diesen komischen Pfaden“)
 */
export function dateiZeile(parent: HTMLElement, pfad: string, label = "Datei"): HTMLDivElement {
  const row = wertZeile(parent, label, dateiName(pfad));
  const v = row.querySelector<HTMLElement>(".tool-kv-val");
  const ort = dateiOrt(pfad);
  if (v) {
    v.title = pfad;
    if (ort) {
      const o = document.createElement("span");
      o.className = "tool-kv-ort";
      o.textContent = ort;
      v.append(" ", o);
    }
  }
  return row;
}

/** Die eine große Zahl (Betrag, Temperatur, Strecke) und ihre Nebenzeile. */
export function betragZeile(parent: HTMLElement, summe: string, neben?: string): HTMLDivElement {
  const el = document.createElement("div");
  el.className = "tool-card-betrag";
  el.textContent = fuerMenschen(summe);
  parent.appendChild(el);
  if (neben) {
    const n = document.createElement("div");
    n.className = "tool-card-betrag-neben";
    n.textContent = fuerMenschen(neben);
    parent.appendChild(n);
  }
  return el;
}

/** Stand-Pillen in einer Reihe („Entwurf“, „#3101“, „läuft“). */
export function chips(parent: HTMLElement, worte: readonly string[]): HTMLDivElement {
  const reihe = document.createElement("div");
  reihe.className = "v-chips";
  for (const w of worte) {
    const c = document.createElement("span");
    c.className = "v-chip";
    c.textContent = fuerMenschen(w);
    reihe.appendChild(c);
  }
  parent.appendChild(reihe);
  return reihe;
}

export type AbsatzArt = "warn" | "hint" | "result";

/** Fließtext in der Karte; `warn` trägt die Kante links, `hint` ist leise, `result` ein ruhiger Kasten. */
export function absatz(text: string, art?: AbsatzArt): HTMLParagraphElement {
  const p = document.createElement("p");
  p.className = art ? `tool-card-body tool-card-body--${art}` : "tool-card-body";
  p.textContent = fuerMenschen(text);
  return p;
}

/** Die Knopfreihe. */
export function aktionsLeiste(parent?: HTMLElement): HTMLDivElement {
  const reihe = document.createElement("div");
  reihe.className = "tool-actions";
  parent?.appendChild(reihe);
  return reihe;
}

export interface Knopf {
  text: string;
  primaer?: boolean;
  /** Ein Symbol vor dem Wort — oder dahinter (`symbolHinten`, etwa „öffnet draußen“). */
  symbol?: IconName;
  symbolHinten?: boolean;
  /** Mit Adresse wird der Knopf ein Link; `neuesFenster` setzt target und rel. */
  href?: string;
  neuesFenster?: boolean;
  /** Der Name für den Vorleser, wenn das Wort allein nicht reicht. */
  name?: string;
  onClick?: (ev: MouseEvent) => void;
}

/**
 * EIN Knopf — als `<a>` oder `<button>`, in derselben Gestalt und Höhe.
 * Gemessen vorher: 38 gegen 34 Punkte in derselben Reihe.
 */
export function aktionsKnopf(k: Knopf): HTMLAnchorElement | HTMLButtonElement {
  let el: HTMLAnchorElement | HTMLButtonElement;
  if (k.href) {
    const a = document.createElement("a");
    a.href = k.href;
    a.rel = "noopener noreferrer";
    if (k.neuesFenster) a.target = "_blank";
    el = a;
  } else {
    const b = document.createElement("button");
    b.type = "button";
    el = b;
  }
  el.className = k.primaer ? "tool-action-btn primary" : "tool-action-btn";
  if (k.name) el.setAttribute("aria-label", k.name);
  // Ein Knopf heißt, was er tut — nie ein Pfad, nie leer.
  const wort = document.createTextNode(ohnePfade(k.text).trim() || k.text);
  if (k.symbol && !k.symbolHinten) el.append(icon(k.symbol, { size: 14 }), wort);
  else if (k.symbol) el.append(wort, icon(k.symbol, { size: 14 }));
  else el.appendChild(wort);
  const onClick = k.onClick;
  if (onClick) el.addEventListener("click", (ev) => onClick(ev as MouseEvent));
  return el;
}

/** „öffnet im neuen Tab“ — Wort plus Außen-Symbol, als Zeile, nicht als Knopf. */
export function linkZeile(href: string, label: string): HTMLAnchorElement {
  const a = document.createElement("a");
  a.className = "tool-list-link tool-list-link--icon";
  a.href = href;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  const lbl = document.createElement("span");
  lbl.textContent = ohnePfade(label).trim() || label;
  a.append(lbl, icon("external", { size: 12 }));
  return a;
}

/** Die Liste der Karte. */
export function liste(parent: HTMLElement): HTMLUListElement {
  const ul = document.createElement("ul");
  ul.className = "tool-list";
  parent.appendChild(ul);
  return ul;
}

/**
 * Ein Eintrag: Hauptzeile (mit Nebenangabe rechts), Zweitzeile(n), Link.
 * Mehrere Zweitzeilen (15.10.2026, Welle 2a: die Zeilen einer Anzeige — „RE-12 · Kunde · 480 €“ je Rechnung)
 * stehen untereinander, jede in derselben Gestalt; leere fallen weg.
 */
export function listenEintrag(primary: string, secondary?: string | readonly string[], meta?: string, href?: string): HTMLLIElement {
  const li = document.createElement("li");
  li.className = "tool-list-item";
  const row = document.createElement("div");
  row.className = "tool-list-row";
  const p = document.createElement("span");
  p.className = "tool-list-primary";
  p.textContent = fuerMenschen(primary, "Eintrag");
  row.appendChild(p);
  const metaText = meta ? fuerMenschen(meta) : "";
  if (metaText) {
    const m = document.createElement("span");
    m.className = "tool-list-meta";
    m.textContent = metaText;
    row.appendChild(m);
  }
  li.appendChild(row);
  for (const zeile of typeof secondary === "string" ? [secondary] : (secondary ?? [])) {
    const zweit = zeile ? fuerMenschen(zeile) : "";
    if (!zweit) continue;
    const s = document.createElement("div");
    s.className = "tool-list-secondary";
    s.textContent = zweit;
    li.appendChild(s);
  }
  if (href) li.appendChild(linkZeile(href, "öffnen"));
  return li;
}

/**
 * Wie viele Einträge eine Karte zeigt, bevor „und N weitere“ folgt.
 * (06.09.2026, Karten-Norm) Vorher sieben Kopien mit vier Grenzen und vier Wortlauten.
 */
export const LISTE_MAX = 8;

/** Fehlen nur ein, zwei Einträge, zeigt die Karte lieber alle: die Zeile kostet dieselbe Höhe und sagt weniger. */
export function kuerzeListe<T>(items: readonly T[], max = LISTE_MAX): { sichtbar: readonly T[]; rest: number } {
  if (items.length <= max + 2) return { sichtbar: items, rest: 0 };
  return { sichtbar: items.slice(0, max), rest: items.length - max };
}

/**
 * Die eine Zeile „und N weitere“ — eine Klasse, ein Wortlaut, ein Platz.
 * Mit `aufklappen` wird die Zeile ein Knopf (die Faltung eines Zuges zeigt
 * die übrigen Karten erst auf Tipp); ohne bleibt sie eine stille Zeile.
 */
export function weitereHinweis(parent: HTMLElement, rest: number, aufklappen?: () => void): HTMLElement | null {
  if (rest <= 0) return null;
  const wort = `und ${rest} weitere`;
  let el: HTMLElement;
  if (aufklappen) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "tool-card-weitere tool-card-weitere--knopf";
    b.setAttribute("aria-expanded", "false");
    b.addEventListener("click", aufklappen);
    el = b;
  } else {
    el = document.createElement("p");
    el.className = "tool-card-weitere";
  }
  el.textContent = wort;
  parent.appendChild(el);
  return el;
}

/** Die leise Zeile IN einer Karte („Keine Termine im Zeitraum.“). */
export function leerHinweis(parent: HTMLElement, text: string): void {
  const p = document.createElement("p");
  p.className = "tool-card-hinweis";
  p.textContent = fuerMenschen(text, "Es gibt dazu nichts anzuzeigen.");
  parent.appendChild(p);
}

/**
 * Die leise Zeile ALS Karte (Martin 2026-07-10, „Haufen Fehlermeldungen“):
 * ein weicher Leerfall statt einer großen Fehlerkarte — die Antwort trägt
 * die Nachricht längst. Dieselbe Wurzel wie jede Karte, damit sie in Zug
 * und Faltung dieselbe Breite und denselben Trenner hat.
 */
export function hinweisKarte(text: string): HTMLDivElement {
  const el = baueKarte("softfail");
  el.classList.add("tool-softfail");
  leerHinweis(el, text);
  return el;
}

/** Der neutrale Satz, wenn ein Fehlertext nur aus Sätzen an das Modell bestand. */
export const FEHLER_ERSATZ = "Das hat nicht geklappt.";

/** Der eine Fehlerkasten für alle Karten (pre.786). */
export function fehlerBlock(text: string): HTMLDivElement {
  const box = document.createElement("div");
  box.className = "tool-error-box";
  box.textContent = fuerMenschen(text, FEHLER_ERSATZ);
  return box;
}

/** Die rohen Teile, die zugeklappt bleiben dürfen — als Rohdaten beschriftet, nichts wird verschwiegen. */
const ROH_KLASSE = "tool-card-raw";

/**
 * Die Endreinigung einer fertigen Karte: jeder sichtbare Textknoten geht
 * durch dieselbe Regel wie die Bausteine. Für alles, was an ihnen vorbei
 * `textContent` setzt (tool-browser-detail, eigene Zeilen).
 *
 *   - Ein Satz an das Modell fällt weg; wird ein Absatz dadurch leer, fällt
 *     der Absatz weg — eine Karte nur mit Titel ist ehrlicher als eine mit
 *     Anweisung.
 *   - Auf Knöpfen, Zusammenfassungen und Beschriftungen wird nur der Pfad
 *     ersetzt — ein Knopf wird nie leer.
 *   - Rohdaten (<pre class="tool-card-raw">) bleiben, wie sie sind.
 */
export function kartenTextSaeubern<T extends HTMLElement>(karte: T): T {
  const laeufer = document.createTreeWalker(karte, NodeFilter.SHOW_TEXT);
  const knoten: Text[] = [];
  for (let n = laeufer.nextNode(); n; n = laeufer.nextNode()) knoten.push(n as Text);
  for (const t of knoten) {
    const eltern = t.parentElement;
    if (!eltern || eltern.closest(`.${ROH_KLASSE}`)) continue;
    const alt = t.data;
    if (!alt.trim()) continue;
    const nieLeer = eltern.closest("button, a, summary, label, .tool-kv-key, .tool-card-title") !== null;
    const neu = textknotenFuerMenschen(alt) || (nieLeer ? ohnePfade(alt) : "");
    if (neu === alt) continue;
    if (neu) {
      t.data = neu;
      continue;
    }
    t.remove();
    // Ein Absatz, der nur die Anweisung trug, steht nicht leer herum.
    if (eltern !== karte && !eltern.textContent?.trim() && !eltern.querySelector("img, svg, input, button, a")) eltern.remove();
  }
  return karte;
}

/** Der Kopf einer Faltung gleicher Werkzeuge: „Websuche · 5-mal“. */
export function gruppenKopf(name: string, anzahl: number): HTMLDivElement {
  const kopf = document.createElement("div");
  kopf.className = "tool-card-title zug-falte-kopf";
  setzeGruppenZahl(kopf, name, anzahl);
  return kopf;
}

export function setzeGruppenZahl(kopf: HTMLElement, name: string, anzahl: number): void {
  kopf.textContent = `${name} · ${anzahl}-mal`;
}
